import { useOutletContext } from "react-router-dom";
import type { MeetingRunContext } from "../MeetingRunContext";
import {
  useImmediateMutationSystem,
  type Mutator,
} from "@/lib/queuedMutations";
import { getVoters } from "../utilities";
import { useRef, useState } from "react";
import { AgendaItemVotesForm } from "@/components/agendaItems/AgendaItemVotesForm";
import { ApprovalButtons } from "@/components/common/ApprovalButtons";
import type { AgendaItemReview } from "@repo/types";
import { DatePicker } from "@repo/ui/components/Base/DatePicker";
import { Button } from "@repo/ui/components/Base/Button";
import { StampIcon } from "lucide-react";
import { CollapsiblePanel } from "../CollapsiblePanel";
import { getSectionById } from "./sections";
import type { MeetingRunSectionProps } from "./meetingRunSectionProps";
import {
  agendaItemRepo,
  type AgendaItemDto,
} from "@/repositories/agendaItemRepo";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { documentRepo } from "@/repositories/documentRepo";

export const MeetingRunVotingSection = ({
  id,
  idSuffix,
  className,
  open,
  onToggle,
}: MeetingRunSectionProps) => {
  const {
    tenantId,
    meeting: _meeting,
    user,
    team,
    userMap,
    agendaItem: _item,
    mutationSystem,
    motionTypeMap,
    noTimestamps,
  } = useOutletContext() as MeetingRunContext;

  const mutationKey = "agendaItem:" + _item!.id;

  const agendaItemMutation: Mutator<AgendaItemDto> = useFutureCallback(
    async (id, data) => {
      await agendaItemRepo.update(tenantId, id, data);
    }
  );
  const agendaItem = useImmediateMutationSystem(
    mutationSystem,
    mutationKey,
    _item!
  );
  const meeting = useImmediateMutationSystem(
    mutationSystem,
    "meeting:" + _meeting.id,
    _meeting
  );

  const votingUsers = getVoters({ meeting, team, userMap });

  const lastMotion =
    agendaItem.review?.motions?.[agendaItem.review?.motions?.length - 1];

  // detect attached meeting minutes, ordinances, and resolutions and suggest also marking them for approval / passing
  const attachedMinutes =
    agendaItem.documents?.filter(
      (doc) =>
        doc.uploadType === "meeting-minutes" && !doc.minutesData?.official
    ) ?? [];
  const attachedOrdinances =
    agendaItem.documents?.filter(
      (doc) =>
        doc.uploadType === "ordinances" &&
        (!doc.ordinanceData?.status || doc.ordinanceData.status === "proposed")
    ) ?? [];
  const attachedResolutions =
    agendaItem.documents?.filter(
      (doc) =>
        doc.uploadType === "resolutions" &&
        (!doc.resolutionData?.status ||
          doc.resolutionData.status === "proposed")
    ) ?? [];

  const minutesDocumentCount =
    attachedMinutes.length > 1 ? ` (${attachedMinutes.length} documents)` : "";
  const ordinanceDocumentCount =
    attachedOrdinances.length > 1
      ? ` (${attachedOrdinances.length} documents)`
      : "";
  const resolutionDocumentCount =
    attachedResolutions.length > 1
      ? ` (${attachedResolutions.length} documents)`
      : "";

  const [approvedMinutes, setApprovedMinutes] = useState(false);
  const [approvedOrdinance, setApprovedOrdinance] = useState<
    false | "approved" | "rejected"
  >(false);
  const [approvedResolution, setApprovedResolution] = useState<
    false | "approved" | "rejected"
  >(false);

  const dateRef = useRef<HTMLDivElement>(null);

  return (
    <CollapsiblePanel
      id={id}
      idSuffix={idSuffix}
      title={getSectionById("voting").title}
      className={className}
      open={open}
      onToggle={onToggle}
    >
      <div className="flex flex-col gap-4">
        <h1 className="mt-5 text-2xl font-bold">Voting</h1>

        <AgendaItemVotesForm
          votes={agendaItem.review?.votes}
          onVotesChange={(votes, source) => {
            const review = Object.assign({}, agendaItem.review);
            review.votes = Object.assign({}, review.votes, votes);

            if (review.votes.users?.length === votingUsers.present.length) {
              // 51%: yes > no
              review.status =
                !review.votes.yes && !review.votes.no
                  ? "deferred"
                  : review.votes.yes > review.votes.no
                    ? "approved"
                    : "rejected";
              review.reviewedBy = user.id;
              if (!noTimestamps) review.timestampEnd = Date.now();
            } else if (source === "clearAll") {
              // when clearing all, reset status to pending
              review.status = "pending";
            }

            mutationSystem.debounce(
              mutationKey,
              agendaItemMutation,
              { review },
              agendaItem.id
            );
          }}
          votingUsers={votingUsers.all}
          absentUsers={votingUsers.absent}
        />

        <ApprovalButtons
          label="Approval"
          value={agendaItem.review?.status ?? "pending"}
          deferredText={
            (lastMotion &&
              lastMotion.status === "approved" &&
              motionTypeMap.item[lastMotion.motionId]?.deferredReason) ||
            undefined
          }
          disabled={agendaItem.review?.seconded === "none"}
          onValueChange={(value) => {
            const timestampEnd = noTimestamps
              ? {}
              : { timestampEnd: Date.now() };

            mutationSystem.debounce(
              mutationKey,
              agendaItemMutation,
              {
                review: {
                  ...agendaItem.review,
                  meetingId: meeting.id,
                  status: value as AgendaItemReview["status"],
                  reviewedBy: user.id,
                  ...timestampEnd,
                },
              },
              agendaItem.id
            );
            setTimeout(() => {
              // if deferred, dateRef.current will be the node to scroll to
              dateRef.current?.scrollIntoView();
            }, 200);
          }}
        />

        {agendaItem.review?.status === "deferred" && (
          <DatePicker
            ref={dateRef}
            id="requestedApprovalDate"
            label="Review Deferred Date"
            containerStyle="mt-4"
            date={agendaItem.requestedApprovalDate}
            onDateChange={(date) => {
              mutationSystem.debounce(
                mutationKey,
                agendaItemMutation,
                { requestedApprovalDate: date },
                agendaItem.id
              );
            }}
          />
        )}

        {/* 'Approve meeting minutes' / 'Pass or Fail ordinance' convenience buttons */}
        <div>
          {!!attachedMinutes.length &&
            agendaItem.review?.status === "approved" && (
              <Button
                disabled={approvedMinutes}
                onClick={async () => {
                  // approve prior meeting minutes
                  setApprovedMinutes(true);

                  for (const doc of attachedMinutes) {
                    await documentRepo.update(tenantId, doc.id, {
                      minutesData: { ...doc.minutesData, official: true },
                    });
                  }
                }}
              >
                <StampIcon className="mr-2" /> Mark Minutes as Officially
                Approved
                {minutesDocumentCount}
              </Button>
            )}
          {!!attachedOrdinances.length &&
            (agendaItem.review?.status === "approved" ? (
              <Button
                disabled={approvedOrdinance === "approved"}
                onClick={async () => {
                  // pass ordinance
                  setApprovedOrdinance("approved");

                  for (const doc of attachedOrdinances) {
                    await documentRepo.update(tenantId, doc.id, {
                      ordinanceData: { ...doc.ordinanceData, status: "passed" },
                    });
                  }
                }}
              >
                <StampIcon className="mr-2" /> Mark Ordinance as Passed
                {ordinanceDocumentCount}
              </Button>
            ) : agendaItem.review?.status === "rejected" ? (
              <Button
                disabled={approvedOrdinance === "rejected"}
                variant="destructive"
                onClick={async () => {
                  // fail ordinance
                  setApprovedOrdinance("rejected");

                  for (const doc of attachedOrdinances) {
                    await documentRepo.update(tenantId, doc.id, {
                      ordinanceData: { ...doc.ordinanceData, status: "failed" },
                    });
                  }
                }}
              >
                <StampIcon className="mr-2" /> Mark Ordinance as Failed
                {ordinanceDocumentCount}
              </Button>
            ) : null)}
          {!!attachedResolutions.length &&
            (agendaItem.review?.status === "approved" ? (
              <Button
                disabled={approvedResolution === "approved"}
                onClick={async () => {
                  // pass resolution
                  setApprovedResolution("approved");

                  for (const doc of attachedResolutions) {
                    await documentRepo.update(tenantId, doc.id, {
                      resolutionData: {
                        ...doc.resolutionData,
                        status: "passed",
                      },
                    });
                  }
                }}
              >
                <StampIcon className="mr-2" /> Mark Resolution as Passed
                {resolutionDocumentCount}
              </Button>
            ) : agendaItem.review?.status === "rejected" ? (
              <Button
                disabled={approvedResolution === "rejected"}
                variant="destructive"
                onClick={async () => {
                  // fail resolution
                  setApprovedResolution("rejected");

                  for (const doc of attachedResolutions) {
                    await documentRepo.update(tenantId, doc.id, {
                      resolutionData: {
                        ...doc.resolutionData,
                        status: "failed",
                      },
                    });
                  }
                }}
              >
                <StampIcon className="mr-2" /> Mark Resolution as Failed
                {resolutionDocumentCount}
              </Button>
            ) : null)}
        </div>
      </div>
    </CollapsiblePanel>
  );
};
